import { Link, useParams } from "react-router-dom";
import { getTicket } from "../lib/api";
import { useApi } from "../lib/useApi";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/Badge";
import { Markdown } from "../lib/markdown";
import { ArticleBody } from "../lib/html";
import { formatDate, ticketStatus } from "../lib/format";
import type { TicketComment } from "../lib/types";

/**
 * A single request (GET /tickets/:id). The portal API returns the ticket row
 * (ownership-enforced) plus its PUBLIC conversation — internal notes are excluded
 * server-side. The first comment is the contact's own opening message; replies
 * land in the same thread.
 */
export function TicketDetailPage() {
  const { id = "" } = useParams();
  const { data, loading, error, reload } = useApi((signal) => getTicket(id, signal), [id]);

  if (loading) return <Loading label="Loading request…" />;

  if (error) {
    const notFound = error.toLowerCase().includes("not found");
    if (notFound) {
      return (
        <EmptyState title="Request not found">
          <p className="muted">We couldn’t find that request, or it isn’t one of yours.</p>
          <Link to="/tickets" className="btn small">
            Back to my requests
          </Link>
        </EmptyState>
      );
    }
    return <ErrorState error={error} onRetry={reload} />;
  }

  const ticket = data?.ticket;
  if (!ticket) {
    return (
      <EmptyState title="Request not found">
        <Link to="/tickets" className="btn small">
          Back to my requests
        </Link>
      </EmptyState>
    );
  }

  const comments = ticket.comments ?? [];

  return (
    <div className="ticket-detail">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/tickets">My requests</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">#{ticket.number}</span>
      </nav>

      <div className="page-head">
        <div>
          <h1>{ticket.subject}</h1>
          <p className="sub">
            Request #{ticket.number} · opened {formatDate(ticket.created_at)}
          </p>
        </div>
        <StatusBadge statusId={ticket.status_id} />
      </div>

      <div className="ticket-grid">
        <section className="card">
          <div className="card-head">
            <h2>Conversation</h2>
          </div>
          <div className="card-body">
            {comments.length === 0 ? (
              <p className="muted">No messages on this request yet.</p>
            ) : (
              <ol className="convo" aria-label="Request conversation">
                {comments.map((c, idx) => (
                  <Message key={c.id} comment={c} isOpening={idx === 0} />
                ))}
              </ol>
            )}
            <p className="muted hint" style={{ marginTop: 16 }}>
              Replies from our team are also sent to your email. Reply there to add to this
              request.
            </p>
          </div>
        </section>

        <aside className="card">
          <div className="card-head">
            <h2>Details</h2>
          </div>
          <div className="card-body">
            <dl className="kv">
              <dt>Status</dt>
              <dd>{ticketStatus(ticket.status_id).label}</dd>
              <dt>Request #</dt>
              <dd>{ticket.number}</dd>
              <dt>Opened</dt>
              <dd>{formatDate(ticket.created_at)}</dd>
              <dt>Last updated</dt>
              <dd>{formatDate(ticket.updated_at)}</dd>
              {ticket.solved_at ? (
                <>
                  <dt>Solved</dt>
                  <dd>{formatDate(ticket.solved_at)}</dd>
                </>
              ) : null}
              {ticket.closed_at ? (
                <>
                  <dt>Closed</dt>
                  <dd>{formatDate(ticket.closed_at)}</dd>
                </>
              ) : null}
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}

/** One conversation message. The API doesn't attribute authors on the portal
 *  surface, but the FIRST comment is structurally the contact's own opening
 *  message (created with the ticket); later entries are thread updates. */
function Message({ comment, isOpening }: { comment: TicketComment; isOpening: boolean }) {
  return (
    <li className={`msg${isOpening ? " you" : ""}`}>
      <div className="msg-head">
        <span className="msg-author">{isOpening ? "You" : "Update"}</span>
        <span className="msg-time muted">{formatDate(comment.created_at)}</span>
      </div>
      <div className="msg-body">
        {/* Agent replies authored in the WYSIWYG composer arrive as HTML; the
            ArticleBody renderer sanitizes (scripts/handlers stripped) before
            display. Plain-text/markdown bodies keep the Markdown renderer. */}
        {/^\s*</.test(comment.body) ? <ArticleBody source={comment.body} /> : <Markdown source={comment.body} />}
      </div>
    </li>
  );
}
